import { Box, Tooltip } from '@mui/material';
import { getTierIcon, getTierColor } from '../icons/TierIcons';

import MasterBadge from '../../assets/tier-badges/master-badge.svg';

export type SubscriptionTier = 'free' | 'trial' | 'launch' | 'rise' | 'summit' | 'expired';
export type UserRole = 'super_admin' | 'master' | 'admin' | 'employee' | 'client';

interface TierBadgeProps {
  tier: SubscriptionTier;
  userRole?: UserRole;
  size?: number;
  showTooltip?: boolean;
}

const tierDisplayNames: Record<string, string> = {
  free: 'Free Plan',
  trial: 'Free Trial',
  launch: 'Launch Plan',
  rise: 'Rise Plan',
  summit: 'Summit Plan',
  expired: 'Expired',
};

// Map legacy tiers to new tiers (for backwards compatibility with old data)
const mapLegacyTier = (tier: string): string => {
  const legacyMap: Record<string, string> = {
    bronze: 'launch',
    silver: 'rise',
    gold: 'summit',
    platinum: 'summit',
  };
  return legacyMap[tier] || tier;
};

// Master badge config for super_admin users
const masterConfig = {
  src: MasterBadge,
  label: 'Platform Master',
  color: '#FFD700',
};

export default function TierBadge({ tier, userRole, size = 24, showTooltip = true }: TierBadgeProps) {
  // Super admin users get the Master badge instead of tier badge
  const isMaster = userRole === 'super_admin';

  if (isMaster) {
    const badge = (
      <Box
        component="img"
        src={masterConfig.src}
        alt={masterConfig.label}
        sx={{
          width: size * 1.5, // Master badge is wider
          height: size,
          display: 'block',
        }}
      />
    );

    if (showTooltip) {
      return (
        <Tooltip title={masterConfig.label} arrow>
          {badge}
        </Tooltip>
      );
    }

    return badge;
  }

  // Map legacy tier names to new ones
  const mappedTier = mapLegacyTier(tier);
  const TierIcon = getTierIcon(mappedTier);
  const tierColor = getTierColor(mappedTier);
  const tierLabel = tierDisplayNames[mappedTier] || 'Free Plan';

  const badge = (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: 'white',
        border: `2px solid ${tierColor}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <TierIcon sx={{ fontSize: size * 0.6, color: tierColor }} />
    </Box>
  );

  if (showTooltip) {
    return (
      <Tooltip title={tierLabel} arrow>
        {badge}
      </Tooltip>
    );
  }

  return badge;
}

export { tierDisplayNames };
